/**
 * Slice 20a — Property unit baseline items (durable fixtures, not restock Inventory).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export interface PropertyUnitItem {
  id: string;
  property_id: string;
  room: string | null;
  name: string;
  photo_url: string | null;
  /** Slice 57 — the photo group this item sits in (null = covered by the whole-room photo). */
  photo_group_id?: string | null;
  /** Slice 58 — how many, when it matters (4 sofa cushions). */
  quantity?: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function useUnitItems(propertyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.unitItems.byProperty(propertyId ?? ""),
    queryFn: async (): Promise<PropertyUnitItem[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("property_unit_items")
        .select("*")
        .eq("property_id", propertyId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PropertyUnitItem[];
    },
    enabled: !!propertyId,
  });
}

export function useCreateUnitItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      propertyId: string;
      name: string;
      room?: string | null;
      photoUrl?: string | null;
      sortOrder?: number;
      quantity?: number | null;
      photoGroupId?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("property_unit_items")
        .insert({
          property_id: input.propertyId,
          name: input.name,
          room: input.room ?? null,
          photo_url: input.photoUrl ?? null,
          sort_order: input.sortOrder ?? 0,
          ...(input.quantity != null ? { quantity: input.quantity } : {}),
          ...(input.photoGroupId ? { photo_group_id: input.photoGroupId } : {}),
        })
        .select()
        .single();
      if (error) throw error;
      return data as PropertyUnitItem;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitItems.byProperty(row.property_id),
      });
    },
  });
}

export function useUpdateUnitItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      propertyId: string;
      name?: string;
      room?: string | null;
      photoUrl?: string | null;
      sortOrder?: number;
      photoGroupId?: string | null;
      quantity?: number | null;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name;
      if (input.room !== undefined) patch.room = input.room;
      if (input.photoUrl !== undefined) patch.photo_url = input.photoUrl;
      if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
      if (input.photoGroupId !== undefined) patch.photo_group_id = input.photoGroupId;
      if (input.quantity !== undefined) patch.quantity = input.quantity;
      const { data, error } = await supabase
        .from("property_unit_items")
        .update(patch)
        .eq("id", input.id)
        .select()
        .single();
      if (error) throw error;
      return data as PropertyUnitItem;
    },
    onSuccess: (_row, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitItems.byProperty(variables.propertyId),
      });
    },
  });
}

export function useDeleteUnitItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; propertyId: string }) => {
      const { error } = await supabase.from("property_unit_items").delete().eq("id", input.id);
      if (error) throw error;
      return input;
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitItems.byProperty(variables.propertyId),
      });
    },
  });
}
